/**
 * Edges — the tenant-facing infrastructure page (#270).
 *
 * Two stacked sections, never tabs:
 *   1. "Your edges"      — devices the org owns, clickable into device detail.
 *   2. "SimCheck network" — LOCATIONS with their capacity, not devices.
 *
 * Both are visible at once on purpose. The bug this page fixes was a customer
 * seeing "New York — 4 modems" on Locations and an empty My Devices table, with
 * nothing on either screen saying those modems belong to SimCheck. Tabs would
 * reproduce that, since one half would still be hidden behind a click.
 *
 * Tenants never see shared hardware as devices — only as the capacity numbers
 * in the network section. See plans/edges-page-shared-vs-owned.md.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { CentralEdgeDevice } from '@simcheck/shared-types';
import { AppShell } from '@/components/layout/AppShell';
import { Button, Card, Alert, Badge } from '@/components/ui';
import { EdgeDeviceTable, useTierVisibleDevices } from '@/components/edges/EdgeDeviceTable';
import { NetworkCapacitySection } from '@/components/edges/NetworkCapacitySection';
import { useNetworkCapacity } from '@/hooks/useNetworkCapacity';
import { edgeDeviceService } from '@/services/edgeDeviceService';
import { useAuth } from '@/contexts/AuthContext';
import { RefreshCw, Server, ArrowRight, Plus, Globe, Signal, Wifi, MapPin } from 'lucide-react';

export default function Edges() {
  const navigate = useNavigate();
  const { activeOrganization, activeOrganizationId, isSuperAdmin, userRole } = useAuth();

  const [devices, setDevices] = useState<CentralEdgeDevice[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const capacity = useNetworkCapacity();

  // Sites live here rather than in the left nav (#270): a tenant's locations
  // only mean anything next to the devices standing at them, and the page they
  // open is scoped to the org's own sites. Owners and admins manage them;
  // super-admins get the full estate through the same door.
  const canManageOwnSites = isSuperAdmin || userRole === 'owner' || userRole === 'admin';

  // Bringing your own hardware is the Business/Enterprise half of this page —
  // mirror of the route + nav gate. The page itself stays open to every plan so
  // a Starter customer can still see the network they test on.
  const orgPlan = activeOrganization?.subscription?.plan ?? '';
  const canAddOwnEdge = isSuperAdmin || ['business', 'enterprise'].includes(orgPlan);

  const loadDevices = async () => {
    try {
      setLoadingDevices(true);
      const deviceList = await edgeDeviceService.getDevices(true);
      setDevices(deviceList);
    } catch (error) {
      console.error('[Edges] Failed to load devices:', error);
    } finally {
      setLoadingDevices(false);
    }
  };

  useEffect(() => {
    loadDevices();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([loadDevices(), capacity.refresh()]);
    setRefreshing(false);
  };

  // "Your edges" is strictly the active org's own fleet — for super-admins too.
  // The cross-org global view stays at /admin/edge-devices; mixing other
  // tenants' devices into a section titled "Your edges" would be a lie.
  const ownedDevices = useMemo(
    () =>
      activeOrganizationId
        ? devices.filter((d) => (d as any).ownerOrgId === activeOrganizationId)
        : [],
    [devices, activeOrganizationId],
  );

  const tierVisibleOwned = useTierVisibleDevices(ownedDevices);

  // Shared capacity only — the org's own sites belong with their devices above.
  const networkLocations = useMemo(
    () => capacity.locations.filter((l) => l.ownership === 'simcheck'),
    [capacity.locations],
  );

  const ownSiteCount = capacity.locations.length - networkLocations.length;
  const hasOwnedDevices = tierVisibleOwned.length > 0;

  const networkAvailable = networkLocations.reduce(
    (sum, l) => sum + (l.availableModemCount || 0),
    0,
  );
  const networkTotal = networkLocations.reduce((sum, l) => sum + (l.modemCount || 0), 0);

  return (
    <AppShell>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Edges</h1>
            <p className="text-sm text-muted-foreground mt-1">
              The edge devices your tests run on — SimCheck's network, plus any you own.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing || loadingDevices || capacity.loading}
              icon={<RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />}
            >
              Refresh
            </Button>
            {canManageOwnSites && (
              <Button
                variant="outline"
                onClick={() => navigate('/locations')}
                icon={<MapPin className="w-4 h-4" />}
              >
                <span className="hidden sm:inline">Manage my sites</span>
                <span className="sm:hidden">Sites</span>
              </Button>
            )}
            {canAddOwnEdge && (
              <Button
                variant="default"
                onClick={() => navigate('/edges/provisioning')}
                icon={<Plus className="w-4 h-4" />}
              >
                <span className="hidden sm:inline">Add device</span>
                <span className="sm:hidden">Add</span>
              </Button>
            )}
          </div>
        </div>

        {/* Stats — one row spanning BOTH halves, so the page opens with the
            whole picture: what you own and what SimCheck runs for you. */}
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Card variant="metric" padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Your devices
                </div>
                <div className="text-2xl font-mono font-bold text-foreground">
                  {tierVisibleOwned.length}
                </div>
              </div>
              <div className="w-12 h-12 bg-accent-blue/10 rounded-lg flex items-center justify-center">
                <Server className="w-6 h-6 text-accent-blue" />
              </div>
            </div>
          </Card>

          <Card variant="metric" padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Yours online
                </div>
                <div className="text-2xl font-mono font-bold text-accent-green">
                  {tierVisibleOwned.filter((d) => d.status === 'online').length}
                </div>
              </div>
              <div className="w-12 h-12 bg-accent-green/10 rounded-lg flex items-center justify-center">
                <Wifi className="w-6 h-6 text-accent-green" />
              </div>
            </div>
          </Card>

          <Card variant="metric" padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Network locations
                </div>
                <div className="text-2xl font-mono font-bold text-foreground">
                  {networkLocations.length}
                </div>
              </div>
              <div className="w-12 h-12 bg-accent-purple/10 rounded-lg flex items-center justify-center">
                <Globe className="w-6 h-6 text-accent-purple" />
              </div>
            </div>
          </Card>

          <Card variant="metric" padding="lg">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">
                  Modems available
                </div>
                <div className="text-2xl font-mono font-bold text-foreground">
                  {networkAvailable}
                  <span className="text-base text-muted-foreground/70">/{networkTotal}</span>
                </div>
              </div>
              <div className="w-12 h-12 bg-accent-blue/10 rounded-lg flex items-center justify-center">
                <Signal className="w-6 h-6 text-accent-blue" />
              </div>
            </div>
          </Card>
        </div>

        {capacity.error && <Alert variant="destructive">{capacity.error}</Alert>}

        {/* ---- Your edges ---- */}
        <Card className="overflow-hidden">
          <div className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <Server className="w-5 h-5 text-primary" />
              <h2 className="font-semibold text-foreground">Your edges</h2>
              <Badge variant="outline" className="text-xs">
                {tierVisibleOwned.length} device{tierVisibleOwned.length === 1 ? '' : 's'}
              </Badge>
            </div>
          </div>

          <p className="px-4 pb-4 text-sm text-muted-foreground">
            Edge devices owned by {activeOrganization?.name || 'your organization'}.
          </p>

          <div className="border-t border-border p-4">
            {loadingDevices || hasOwnedDevices ? (
              <EdgeDeviceTable
                devices={ownedDevices}
                loading={loadingDevices}
                activeOrganizationId={activeOrganizationId}
                onRowClick={(device) => navigate(`/edges/${device.id}`)}
                emptyMessage="No edge devices owned by your organization yet."
                searchPlaceholder="Search your devices by name, ID, location, or IP..."
                showFilters={ownedDevices.length > 5}
                defaultPageSize={10}
              />
            ) : (
              /* Zero owned devices collapses to a one-line banner rather than an
                 empty table — an empty grid reads as "something is broken", which
                 is the confusion this page exists to remove. */
              <div className="flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                  <Server className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      You don't own any edge devices.
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {canAddOwnEdge
                        ? 'Your tests run on the SimCheck network below. You can also add your own device to test from your own site.'
                        : 'Your tests run on the SimCheck network below.'}
                    </p>
                  </div>
                </div>
                {canAddOwnEdge && (
                  <Button
                    variant="outline"
                    className="shrink-0"
                    onClick={() => navigate('/edges/provisioning')}
                    icon={<ArrowRight className="h-4 w-4" />}
                  >
                    Bring your own edge
                  </Button>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* ---- SimCheck network ---- */}
        <NetworkCapacitySection locations={networkLocations} loading={capacity.loading} />
      </div>
    </AppShell>
  );
}
